import storage from '../common/utils/storage'

import http, { Query, Method } from './http'
import getApiToken from './getApiToken'
import baseApiUrl from './baseApiUrl'

export const authInterceptor = (response: Response): Response => {
    if (response.status === 401 && new URL(response.url).pathname !== '/api/auth/login') {
        storage.removeItem('token')
        location.pathname = '/login'
    }
    return response
}

export const defaultParseResponse = <APIResponse>(response: Response): Promise<APIResponse> =>
    response
        .text()
        .then((text) => {
            if (!text) return null
            return JSON.parse(text)
        })
        .then((result) => {
            if (response.status < 400) return result
            return Promise.reject(result)
        })

function api<Response = Record<string, unknown>, Body = Record<string, unknown> | Array<unknown>>({
    method,
    path,
    base = baseApiUrl,
    query,
    body,
    headers,
    parseResponse,
}: Props<Response, Body>): Promise<Response> {
    return http({
        url: `${base}${path.startsWith('/') ? path : `/${path}`}`,
        query,
        method,
        headers: {
            ...(getApiToken() && { Authorization: `Bearer ${getApiToken()}` }),
            ...(!(body instanceof FormData) && {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            }),
            ...headers,
        },
        body: body instanceof FormData ? body : JSON.stringify(body),
    })
        .then(authInterceptor)
        .then((response) => {
            if (parseResponse) return parseResponse(response, defaultParseResponse)
            return defaultParseResponse<Response>(response)
        })
}

export default Object.assign(api, {
    get: <Response = Record<string, unknown>, Body = Record<string, unknown> | Array<unknown>>(
        props: Omit<Props<Response, Body>, 'method'>
    ): Promise<Response> => api<Response, Body>({ ...props, method: 'GET' }),

    post: <Response = Record<string, unknown>, Body = Record<string, unknown> | Array<unknown>>(
        props: Omit<Props<Response, Body>, 'method'>
    ): Promise<Response> => api<Response, Body>({ ...props, method: 'POST' }),

    put: <Response = Record<string, unknown>, Body = Record<string, unknown> | Array<unknown>>(
        props: Omit<Props<Response, Body>, 'method'>
    ): Promise<Response> => api<Response, Body>({ ...props, method: 'PUT' }),

    del: <Response = Record<string, unknown>, Body = Record<string, unknown> | Array<unknown>>(
        props: Omit<Props<Response, Body>, 'method'>
    ): Promise<Response> => api<Response, Body>({ ...props, method: 'DELETE' }),
})

export type Props<APIResponse, Body> = {
    path: string
    base?: string
    query?: Query
    method?: Method
    headers?: Record<string, string>
    body?: Body
    parseResponse?: (response: Response, defaultParser: (response: Response) => Promise<APIResponse>) => APIResponse
}

export type { Query, Method }
